import { Box, Flex, Grid, Stack, Text } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { ChatInput } from '#modules/chat/components/Chats/ChatInput';
import { PeerSessionIdentity } from '#modules/chat/components/Chats/PeerSessionStatus';
import { PeerSessionPreview } from '#modules/chat/components/Chats/PeerSessionStatus/preview';
import { peerSessionsQueryKey } from '#modules/chat/components/Chats/PeerSessionStatus/usePeerSessions';
import { SessionChatBar } from '#modules/chat/components/Chats/SessionChatBar';
import { PostPreviewTuned } from '#modules/finder/components/PostCardPreview/PostPreviewTuned';
import { SessionDetailsDialog } from '#modules/finder/features/posts/components/Post/SessionDetailsDialog';
import { SessionNotificationSummaryContent } from '#modules/finder/features/posts/components/Post/SessionDetailsDialog/NotificationSummary';
import {
  CompactSessionRow,
  SessionCard,
  statusConfig,
} from '#modules/finder/features/posts/components/Post/SessionHistory';
import { getPlainPosts } from '#modules/finder/features/posts/model/posts';
import type { Post } from '#modules/finder/features/posts/types';
import { InviteSessionPicker } from '#modules/user/features/friends/components/Friends/InviteSessionPicker';
import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { CallMini } from '#features/calls/components/CallOverlay/CallMini';
import { CallStage } from '#features/calls/components/CallOverlay/CallStage';
import type { useCallRoom } from '#features/calls/hooks/useCallRoom';
import { useFormatDateRelative, useFormatDuration } from '#features/date';
import type { Game } from '#features/game/model/services';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import type { SessionRoomPhase } from '#features/session-room/components/RoomChrome';
import { SessionRoomBar } from '#features/session-room/components/SessionRoomBar';
import { SessionRoomPill } from '#features/session-room/components/SessionRoomPill';
import { SessionRoomSheet } from '#features/session-room/components/SessionRoomSheet';
import { SessionRoomWindow } from '#features/session-room/components/SessionRoomWindow';
import type { SessionRoomEntry } from '#features/session-room/model/roomSessions';
import { useSessionRoomStore } from '#features/session-room/model/sessionRoomStore';
import { CommentIcon, Panel, SectionHeading } from '#features/ui';
import { Button } from '#features/ui/components/Button';
import { DialogRoot } from '#features/ui/components/Dialog';
import { IconButton } from '#features/ui/components/IconButton';
import { MicButton } from '#features/voice/components/MicButton';
import { TierMicCluster } from '#features/voice/components/SessionVoiceTier/TierCluster';
import { TierCounts } from '#features/voice/components/SessionVoiceTier/TierCounts';
import {
  VoiceRoomOwnerContext,
  type VoiceRoomOwner,
} from '#features/voice/components/VoiceRoomProvider';
import { NativeAudioRoutesContext } from '#features/voice/hooks/useNativeAudioRoutes';
import { voiceRoomQueryKey } from '#features/voice/model/voice';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import type {
  NativeAudioRoutes,
  NativeAudioSelection,
} from '#features/voice/utils/nativeAudioRoutes';

/**
 * Витрина комнаты сессии: все её виды и все состояния на одной странице, без живой сессии.
 *
 * Зачем. Комната показывается только тому, кто прямо сейчас принят в состав идущей сессии, – то
 * есть посмотреть на неё глазами нельзя, не собрав сессию с живыми людьми, а половину состояний
 * («чат ещё создаётся», «голос в Discord», «никого в голосе») так не получить вовсе. Здесь стоят
 * НАСТОЯЩИЕ компоненты продукта с подставленными данными: что видно на этой странице, то увидит
 * и человек.
 *
 * Чего она не делает: кнопки внутри никуда не ведут – сессии за ними нет, голос не звучит.
 *
 * Живёт рядом с остальными `/preview/*` и удаляется одним движением вместе с ними.
 */
export const Route = createFileRoute('/{-$locale}/_layout/preview/session-room')({
  validateSearch: (search: Record<string, unknown>) => ({
    peer: search.peer === true || search.peer === 'true',
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    role: typeof search.role === 'string' ? search.role : 'guest',
    clash: typeof search.clash === 'string' ? search.clash : 'none',
    own: search.own === true || search.own === 'true',
    long: search.long === true || search.long === 'true',
    mini: search.mini === true || search.mini === 'true',
    controls: search.controls === true || search.controls === 'true',
    access: search.access === true || search.access === 'true',
    audio: typeof search.audio === 'string' ? search.audio : '',
    audit: search.audit === true || search.audit === 'true',
    stability: search.stability === true || search.stability === 'true',
    squad: typeof search.squad === 'string' ? search.squad : '',
    notification: typeof search.notification === 'string' ? search.notification : '',
  }),
  component: SessionRoomPreviewFixture,
  ssr: false,
});

const POST_ID = '11111111-1111-4111-8111-111111111111';
/*
  Своя заявка на фазу: полоска над перепиской спрашивает сессию по `postId`, и с одним на всех
  все три блока показали бы состояние идущей сессии.
*/
const POST_ID_UPCOMING = '1111aaaa-1111-4111-8111-111111111111';
const POST_ID_ENDED = '1111bbbb-1111-4111-8111-111111111111';
const POST_ID_CANCELLED = '1111dddd-1111-4111-8111-111111111111';
const SESSION_ID = '22222222-2222-4222-8222-222222222222';
const CHAT_ID = '33333333-3333-4333-8333-333333333333';
const HOST_ID = '44444444-4444-4444-8444-444444444444';
const MATE_ID = '55555555-5555-4555-8555-555555555555';
const THIRD_ID = '66666666-6666-4666-8666-666666666666';
const GAME_ID = '77777777-7777-4777-8777-777777777777';

const minutesFromNow = (minutes: number) => new Date(Date.now() + minutes * 60_000).toISOString();

/** Состав: хост и двое принятых – столько, чтобы в ярусе были и лица, и дробь. */
const members = [
  { memberId: HOST_ID, status: 'ACCEPTED', displayName: 'Salt', avatar: null },
  { memberId: MATE_ID, status: 'ACCEPTED', displayName: 'Nova', avatar: null },
  { memberId: THIRD_ID, status: 'ACCEPTED', displayName: 'Kir', avatar: null },
];

/**
 * Заявка с сессией так, как её отдаёт запрос комнаты. Фаза задаётся временем старта и конца, а
 * не отдельным полем: именно так её считает продукт.
 */
const entry = (over: Partial<SessionRoomEntry> = {}): SessionRoomEntry => ({
  sessionId: SESSION_ID,
  postId: POST_ID,
  hostId: HOST_ID,
  status: 'ACTIVE',
  startedAt: minutesFromNow(-18),
  expiredAt: minutesFromNow(42),
  /*
    Логотип настоящий, хоть и чужой игры: без него витрина прятала ровно то, ради чего на пилюлю
    и смотрят первым делом - кружок игры с кольцом «идёт сейчас». Файл уже лежит в витрине
    переписок сессий.
  */
  game: {
    id: GAME_ID,
    name: 'Apex Legends',
    icon: '/preview/session-chats/apex-mark.svg',
    logo: null,
  },
  teamLimit: 5,
  voiceMode: 'PLATFORM',
  acceptedMembersCount: 3,
  voiceConnectedCount: 2,
  voiceRoomStatus: 'OPEN',
  chatId: CHAT_ID,
  unreadCount: 3,
  lastMessageText: 'го на мид, я уже в голосе',
  lastMessageAt: minutesFromNow(-2),
  lastMessageId: '88888888-8888-4888-8888-888888888888',
  members,
  ...over,
});

type Case = { label: string; note?: string; phase: SessionRoomPhase; entry: SessionRoomEntry };

const PHASES: Case[] = [
  {
    label: 'До старта – сессия начнётся через 6 минут',
    note: 'Комната выходит на экран за десять минут до старта. В мини-виджете – компактный обратный отсчёт, голос уже открыт.',
    phase: 'upcoming',
    entry: entry({
      postId: POST_ID_UPCOMING,
      startedAt: minutesFromNow(6),
      expiredAt: minutesFromNow(66),
      voiceConnectedCount: 0,
      unreadCount: 0,
      lastMessageText: null,
      lastMessageAt: null,
      lastMessageId: null,
    }),
  },
  {
    label: 'Идёт – 42 минуты до конца, двое в голосе',
    note: 'Фаза сессии названа словами. Состав и присутствие в голосе показаны отдельно.',
    phase: 'live',
    entry: entry(),
  },
  {
    label: 'Голос в игре',
    note: 'Состав виден, голосовой чат настраивается в игре. Комнаты Teamplay нет.',
    phase: 'live',
    entry: entry({ voiceMode: 'IN_GAME', voiceConnectedCount: 0, voiceRoomStatus: null }),
  },
  {
    label: 'Завершена – переписка остаётся',
    note: 'Голоса больше нет, лента и дверь в неё остаются. Виджет можно закрыть.',
    phase: 'ended',
    entry: entry({
      postId: POST_ID_ENDED,
      status: 'COMPLETED',
      startedAt: minutesFromNow(-70),
      expiredAt: minutesFromNow(-10),
      voiceConnectedCount: 0,
      voiceRoomStatus: 'CLOSED',
    }),
  },
  {
    label: 'Отменена – переписка остаётся',
    note: 'Отмена названа отдельно от завершения. Голос закрыт, а в чат можно вернуться по привычной иконке.',
    phase: 'cancelled',
    entry: entry({
      postId: POST_ID_CANCELLED,
      status: 'CANCELLED',
      startedAt: minutesFromNow(-20),
      expiredAt: minutesFromNow(40),
      voiceConnectedCount: 0,
      voiceRoomStatus: 'CLOSED',
      lastMessageText: 'SESSION_CANCELLED',
      unreadCount: 1,
    }),
  },
];

const EDGE_CASES: Case[] = [
  {
    label: 'Чат ещё создаётся',
    note: 'Первые секунды после создания сессии. На месте иконки чата показывается загрузка с доступным пояснением.',
    phase: 'live',
    entry: entry({ chatId: null, unreadCount: 0, lastMessageText: null, lastMessageId: null }),
  },
  {
    label: 'В переписке пока ни слова',
    phase: 'live',
    entry: entry({
      unreadCount: 0,
      lastMessageText: null,
      lastMessageAt: null,
      lastMessageId: null,
    }),
  },
  {
    label: 'Заявку удалили, переписка осталась',
    note: 'С 20.09.2026 удаление заявки не убивает разговор: команда собралась, и объявление ей больше не нужно. Раньше писать не мог никто, включая тех, кто заявку не трогал.',
    phase: 'live',
    entry: entry({ lastMessageText: 'SESSION_POST_DELETED', unreadCount: 1 }),
  },
  {
    label: 'Последняя строка – служебная',
    note: 'Вместо реплики человека – событие сессии.',
    phase: 'live',
    entry: entry({ lastMessageText: 'SESSION_MEMBER_JOINED', unreadCount: 1 }),
  },
  {
    label: 'Голос выключен в сессии',
    note: 'Лиц нет – слышать некого. Дробь остаётся, но уже про состав: значок людей вместо микрофона.',
    phase: 'live',
    entry: entry({ voiceMode: 'NONE', voiceConnectedCount: 0, voiceRoomStatus: null }),
  },
  {
    label: 'Голос в Discord',
    note: 'Вместо входа в комнату – дверь в канал гильдии.',
    phase: 'live',
    entry: entry({ voiceMode: 'DISCORD', voiceConnectedCount: 0, voiceRoomStatus: null }),
  },
  {
    label: 'Голос в игре',
    note: 'Состав виден, голосовой чат настраивается в игре. Комнаты Teamplay нет.',
    phase: 'live',
    entry: entry({ voiceMode: 'IN_GAME', voiceConnectedCount: 0, voiceRoomStatus: null }),
  },
  {
    label: 'Никого в голосе',
    note: 'Комната открыта и пуста – самый частый вид у начавшейся сессии.',
    phase: 'live',
    entry: entry({ voiceConnectedCount: 0 }),
  },
];

/**
 * Данные кладём прямо в кеш запросов – теми же ключами, какими их читают поверхности.
 *
 * Иначе витрина уходит в сеть за сессией, которой не существует: состав пуст, ярус пишет «0 в
 * голосе», и смотреть становится не на что. Живого медиасервера здесь нет и быть не должно.
 */
const useSessionFixtures = (phases: Case[], isolated = false) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const fixtures = phases.flatMap((item) => {
      const session = item.entry;
      const connectedCount = session.voiceConnectedCount ?? 0;
      return [
        {
          key: ['session-chat-context', session.postId, session.sessionId],
          data: {
            ...session,
            gameId: GAME_ID,
          },
        },
        {
          key: voiceRoomQueryKey(session.sessionId),
          data: {
            sessionId: session.sessionId,
            postId: session.postId,
            status: session.voiceRoomStatus,
            hostId: HOST_ID,
            maxParticipants: session.teamLimit,
            connectedCount,
            participants: session.members.slice(0, connectedCount).map((member) => ({
              userId: member.memberId,
              connected: true,
              host: member.memberId === HOST_ID,
              totalSeconds: 320,
            })),
            livekitUrl: null,
            token: null,
          },
        },
      ];
    });
    // Все ключи принадлежат только витрине. Сетевая проверка несуществующей сессии
    // не должна заменять её показательные данные на null.
    let seeding = false;
    const seed = () => {
      if (seeding) return;
      seeding = true;
      for (const fixture of fixtures) queryClient.setQueryData(fixture.key, fixture.data);
      seeding = false;
    };
    seed();
    const timer = setInterval(seed, 3_000);
    return () => {
      clearInterval(timer);
      // В изолированном кеше смена fixtures обновляет активные запросы на месте.
      // removeQueries отрывает их наблюдателей и оставляет ярус пустым до перезагрузки.
      if (!isolated)
        for (const fixture of fixtures)
          queryClient.removeQueries({ queryKey: fixture.key, exact: true });
    };
  }, [phases, queryClient, isolated]);
};

/**
 * Окно комнаты публикует странице резерв снизу и следит за чужими правками стиля документа – так
 * лента под ним не уезжает под коробку. На витрине окон сразу несколько, и все они пишут одну
 * переменную разными значениями: правка одного будит остальных, и вкладка уходит в вечный
 * пересчёт (проверено – вкладка умирает за четыре секунды). Резерв публикуется только из нижних
 * углов, поэтому витрина ставит верхний: вид окна от этого не меняется ни на пиксель, а спор за
 * переменную исчезает.
 */
const useTopCorner = (enabled = true) => {
  const setCorner = useSessionRoomStore((state) => state.setCorner);

  useEffect(() => {
    if (!enabled) return;
    const previousCorner = useSessionRoomStore.getState().corner;
    setCorner('tr');
    return () => setCorner(previousCorner);
  }, [setCorner, enabled]);
};

const noop = () => undefined;

const Frame = ({
  label,
  note,
  minH,
  naturalMini = false,
  children,
}: {
  label: string;
  note?: string;
  minH?: string;
  /** Preserve the product's 300/374px pill and viewport-width bar for compact QA. */
  naturalMini?: boolean;
  children: ReactNode;
}) => (
  <Stack gap={2}>
    <Text textStyle="label" color="fg.muted" textTransform="uppercase" letterSpacing="0.08em">
      {label}
    </Text>
    {note ? (
      <Text textStyle="caption" color="fg.muted" maxW="60ch">
        {note}
      </Text>
    ) : null}
    {/*
      Поверхности продукта прибиты к краям экрана. `position: relative` на рамке переводит их в её
      координаты, а правило ниже снимает само закрепление – сам компонент при этом не меняется ни
      на строчку, и смотрим мы на настоящую вёрстку.
    */}
    <Panel
      variant="nested"
      position="relative"
      minH={minH}
      p={{ base: 0, md: 4 }}
      overflow="hidden"
      css={
        naturalMini
          ? {
              '& [data-testid="session-room-pill"], & [data-testid="session-room-bar"]': {
                position: 'static',
                inset: 'auto',
              },
              '& [data-testid="session-room-bar"]': { width: '100%' },
            }
          : {
              '& [data-testid="session-room-pill"], & [data-testid="session-room-bar"], & [data-testid="session-room-window"]':
                { position: 'static', inset: 'auto', width: '100%', maxWidth: '400px' },
              '@media (max-width:767px)': {
                '& [data-testid="session-room-pill"]': { display: 'none' },
              },
            }
      }
    >
      {children}
    </Panel>
  </Stack>
);

/** Пилюля и полоса рядом: один и тот же ответ в двух свёрнутых видах. */
const CollapsedPair = ({ item }: { item: Case }) => (
  <Stack gap={3}>
    <SessionRoomPill
      session={item.entry}
      phase={item.phase}
      onExpand={noop}
      onFullscreen={item.entry.chatId ? noop : null}
      onOpenSessionSettings={noop}
      lastMessageAuthor={item.entry.lastMessageText ? 'Nova' : null}
    />
    <SessionRoomBar
      session={item.entry}
      phase={item.phase}
      onExpand={noop}
      onFullscreen={item.entry.chatId ? noop : null}
      onOpenSessionSettings={noop}
      onClose={noop}
      lastMessageAuthor={item.entry.lastMessageText ? 'Nova' : null}
    />
  </Stack>
);

function SessionRoomPreview() {
  const { t } = useLingui();
  const [sheetCase, setSheetCase] = useState<Case | null>(null);
  const { mini, access, controls, audit, stability, long, squad } = Route.useSearch();
  const [joined, setJoined] = useState(
    stability ||
      ['joined', 'connecting', 'reconnecting', 'micDenied', 'audioBlocked', 'remote'].includes(
        squad
      )
  );
  const [speaker, setSpeaker] = useState<string | null>(HOST_ID);
  const [muted, setMuted] = useState(false);
  const [mutedForMe, setMutedForMe] = useState(false);
  const [fixedMini, setFixedMini] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const mobileAnchor = useSessionRoomStore((state) => state.mobileAnchor);
  const setMobileAnchor = useSessionRoomStore((state) => state.setMobileAnchor);
  const [joinedPhase, setJoinedPhase] = useState<'connected' | 'connecting' | 'reconnecting'>(
    squad === 'connecting' || squad === 'reconnecting' ? squad : 'connected'
  );
  const { profile } = useProfile();
  const phases = useMemo(() => {
    const now = Date.now();
    const templates = [
      ...PHASES,
      {
        label: 'Завершена · голосовая комната ещё открыта',
        phase: 'ended' as const,
        entry: entry({
          postId: '1111cccc-1111-4111-8111-111111111111',
          status: 'COMPLETED',
          voiceRoomStatus: 'OPEN',
        }),
      },
    ];
    return templates.map((item, index) => ({
      ...item,
      entry: {
        ...item.entry,
        ...(squad
          ? {
              voiceMode:
                squad === 'discord' || squad === 'discordLink'
                  ? 'DISCORD'
                  : squad === 'inGame'
                    ? 'IN_GAME'
                    : squad === 'none'
                      ? 'NONE'
                      : 'PLATFORM',
              voiceConnectedCount: squad === 'empty' ? 0 : item.entry.voiceConnectedCount,
            }
          : {}),
        sessionId: `22222222-2222-4222-8222-22222222222${index}`,
        startedAt: new Date(
          now + (item.phase === 'upcoming' ? 6 : item.phase === 'live' ? -18 : -70) * 60_000
        ).toISOString(),
        expiredAt: new Date(
          now + (item.phase === 'upcoming' ? 66 : item.phase === 'live' ? 42 : -10) * 60_000
        ).toISOString(),
        members: (squad === 'rosterMax'
          ? [
              ...item.entry.members,
              {
                memberId: '99999999-9999-4999-8999-999999999991',
                status: 'ACCEPTED',
                displayName: 'Ilya',
                avatar: null,
              },
              {
                memberId: '99999999-9999-4999-8999-999999999992',
                status: 'ACCEPTED',
                displayName: 'Alex',
                avatar: null,
              },
            ]
          : item.entry.members
        ).map((member) => ({
          ...member,
          memberId: member.memberId === THIRD_ID && profile?.id && profile.id !== HOST_ID ? profile.id : member.memberId,
          displayName: long
            ? `${member.displayName} ОченьДлинноеИмяУчастникаСессии`
            : member.displayName,
        })),
      },
    }));
  }, [profile?.id, long, squad]);
  useEffect(() => {
    const previous = useVoiceStore.getState();
    if (joined)
      useVoiceStore.setState({
        phase: joinedPhase,
        room: {
          sessionId: phases[squad === 'remote' ? 0 : 1].entry.sessionId,
          postId: POST_ID,
          hostId: HOST_ID,
          livekitUrl: '',
          token: '',
          maxParticipants: 5,
        },
        members: phases[1].entry.members.map((member) => ({
          userId: member.memberId,
          displayName: member.displayName,
          host: member.memberId === HOST_ID,
          connected: true,
          speaking: stability
            ? member.memberId === speaker && !(member.memberId === HOST_ID && muted)
            : true,
          muted: stability && member.memberId === HOST_ID && muted,
        })),
        ...(stability ? { localMuted: { [HOST_ID]: mutedForMe } } : {}),
      });
    const fixtureError =
      squad === 'connectionLost'
        ? 'voice.connection.lost'
        : squad === 'error'
          ? 'voice.connect.failed'
          : null;
    if (fixtureError)
      useVoiceStore.setState({
        phase: 'idle',
        room: null,
        members: [],
        lastError: fixtureError,
        lastErrorSessionId: phases[1].entry.sessionId,
      });
    return () => {
      if (joined || fixtureError) useVoiceStore.setState(previous);
    };
  }, [joined, joinedPhase, phases, stability, speaker, muted, mutedForMe, squad]);
  useSessionFixtures(phases, Boolean(squad));
  useTopCorner(!mini && !access);

  if (audit) return <InGameSurfacesPreview />;

  if (stability)
    return (
      <Stack p={3} gap={4} maxW="440px" w="full" mx="auto">
        <Flex gap={2} wrap="wrap">
          <Button
            size="sm"
            aria-pressed={speaker === HOST_ID}
            onClick={() => setSpeaker(speaker === HOST_ID ? null : HOST_ID)}
          >
            {t`post:voice.widget.host`}
          </Button>
          <Button size="sm" aria-pressed={speaker === MATE_ID} onClick={() => setSpeaker(MATE_ID)}>
            Nova
          </Button>
          <Button size="sm" aria-pressed={muted} onClick={() => setMuted(!muted)}>
            {t`post:voice.room.muteMic`}
          </Button>
          <Button size="sm" aria-pressed={mutedForMe} onClick={() => setMutedForMe(!mutedForMe)}>
            {t`post:voice.widget.muteForMe`}
          </Button>
        </Flex>
        <Frame label={phases[1].label}>
          <SessionRoomWindow
            session={phases[1].entry}
            phase="live"
            onCollapse={noop}
            onFullscreen={noop}
            onClose={noop}
            onOpenInvite={noop}
          />
        </Frame>
        <Frame label="">
          <CollapsedPair item={phases[1]} />
        </Frame>
      </Stack>
    );

  if (controls)
    return (
      <Stack gap={6} minW={0} w="full">
        <DirectCallPreview />
        <VoiceControlsPreview />
      </Stack>
    );

  if (access) {
    return (
      <Stack p={3} gap={5} w="full" minW={0}>
        <SectionHeading>Микрофон: нет доступа</SectionHeading>
        {[false].map((variant) => (
          <Stack key={String(variant)} gap={3}>
            <Text>Обычный режим</Text>
            <Flex
              h="56px"
              px={3}
              align="center"
              bg="bg.surface"
              borderRadius="l2"
              overflow="hidden"
            >
              <TierMicCluster
                density="desktop"
                micEnabled
                micDenied
                onToggleMic={noop}
                onRequestAccess={noop}
                listDevices={async () => []}
                onSelectDevice={noop}
                devicePickerOpen={false}
                onDevicePickerOpenChange={noop}
                onLeave={noop}
              />
            </Flex>
            <Flex
              h="56px"
              px={3}
              align="center"
              bg="bg.surface"
              borderRadius="l2"
              overflow="hidden"
            >
              <MicButton size="44px" micEnabled needsAccess onClick={noop} />
              <Text ml={3} textStyle="caption">
                Мобильная кнопка · 44px
              </Text>
            </Flex>
          </Stack>
        ))}
      </Stack>
    );
  }

  if (mini) {
    return (
      <Stack p={{ base: 3, md: 8 }} gap={6} minW={0} w="full">
        <SectionHeading>Мини-виджет: фазы сессии</SectionHeading>
        <Button
          aria-label="Preview: fixed mini"
          onClick={() => {
            setFixedMini(!fixedMini);
            setJoined(true);
          }}
        >
          Проверить закрепление
        </Button>
        <IconButton aria-label="Preview: joined voice" onClick={() => setJoined(!joined)}>
          <CommentIcon />
        </IconButton>
        <IconButton
          aria-label="Preview: reconnecting voice"
          onClick={() => {
            setJoined(true);
            setJoinedPhase(joinedPhase === 'connected' ? 'reconnecting' : 'connected');
          }}
        >
          <CommentIcon />
        </IconButton>
        {fixedMini ? (
          <>
            <Button
              aria-label="Preview: change anchor"
              onClick={() => setMobileAnchor(mobileAnchor === 'bottom' ? 'top' : 'bottom')}
            >
              Переместить к другому краю
            </Button>
            <SessionRoomBar session={phases[1].entry} phase="live" onExpand={noop} />
            <Stack gap={4} minH="150vh" justify="flex-end">
              <Text textStyle="caption">Нижние действия страницы</Text>
              <Button aria-label="Preview: bottom action" onClick={noop}>
                Сохранить
              </Button>
              <Box data-testid="fixed-mini-composer">
                <ChatInput
                  chatId="preview-fixed-mini"
                  replyTo={null}
                  onCancelReply={noop}
                  textareaRef={composerRef}
                  onSend={async () => true}
                  isSending={false}
                  isBlocked={false}
                  blockedByPeer={false}
                  isTyping={false}
                  onTyping={noop}
                  onStopTyping={noop}
                />
              </Box>
            </Stack>
          </>
        ) : (
          <Grid
            templateColumns={{ base: 'minmax(0, 1fr)', lg: 'repeat(3, minmax(0, 1fr))' }}
            gap={4}
          >
            {phases.map((item) => (
              <Frame key={item.entry.sessionId} label={item.label} naturalMini>
                <CollapsedPair item={item} />
              </Frame>
            ))}
          </Grid>
        )}
      </Stack>
    );
  }

  return (
    <Box p={{ base: 4, md: 8 }} maxW="1180px" w="full" minW={0} mx="auto">
      <Stack gap={12}>
        <Stack gap={2}>
          <SectionHeading>Комната сессии: все виды и все состояния</SectionHeading>
          <Text textStyle="body" color="fg.muted" maxW="78ch">
            Настоящие компоненты продукта с подставленными данными, а не картинки. Кнопки внутри не
            работают: сессии за ними нет. В жизни каждая поверхность прибита к своему краю экрана,
            здесь они стоят в рамках, чтобы их было видно рядом.
          </Text>
        </Stack>

        {phases.map((item) => (
          <Stack key={item.entry.sessionId} gap={4}>
            <Stack gap={1}>
              <Text textStyle="title">{item.label}</Text>
              {item.note ? (
                <Text textStyle="caption" color="fg.muted" maxW="70ch">
                  {item.note}
                </Text>
              ) : null}
            </Stack>

            <Grid templateColumns={{ base: '1fr', lg: '440px 1fr' }} gap={4} alignItems="start">
              <Frame label="Окно – большой экран" minH="220px">
                <SessionRoomWindow
                  session={item.entry}
                  phase={item.phase}
                  onCollapse={noop}
                  onFullscreen={item.entry.chatId ? noop : null}
                  onClose={noop}
                  onOpenSessionSettings={noop}
                  onOpenInvite={noop}
                  lastMessageAuthor="Nova"
                />
              </Frame>

              <Stack gap={4}>
                <Frame label="Пилюля и полоса – свёрнутая комната">
                  <CollapsedPair item={item} />
                </Frame>

                <Frame label="Полоска над перепиской – экран чата сессии">
                  <SessionChatBar postId={item.entry.postId} sessionId={item.entry.sessionId} />
                </Frame>
              </Stack>
            </Grid>
          </Stack>
        ))}

        <Stack gap={4}>
          <Stack gap={1}>
            <Text textStyle="title">Состояния, которые руками не собрать</Text>
            <Text textStyle="caption" color="fg.muted" maxW="70ch">
              Каждое живёт секунды или требует особой сессии. Показаны на пилюле и полосе – именно
              там расходятся слова: в углу написано «2 в голосе», на телефоне «2/5».
            </Text>
          </Stack>
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4} alignItems="start">
            {EDGE_CASES.map((item) => (
              <Frame key={item.label} label={item.label} note={item.note}>
                <CollapsedPair item={item} />
              </Frame>
            ))}
          </Grid>
        </Stack>

        <Stack gap={3}>
          <Stack gap={1}>
            <Text textStyle="title">Раскрытый виджет – телефон, по нажатию на полосу</Text>
            <Text textStyle="caption" color="fg.muted" maxW="70ch">
              Плавает над нижней навигацией, поэтому в ряд его не поставить. Нажмите фазу –
              откроется настоящий виджет; за ручку он переносится к верхнему краю и обратно.
            </Text>
          </Stack>
          <Flex gap={2} wrap="wrap">
            {phases.map((item) => (
              <Panel
                key={item.entry.sessionId}
                variant="nested"
                interactive
                px={4}
                py={2}
                cursor="pointer"
                onClick={() => setSheetCase(item)}
              >
                <Text textStyle="label">{item.label}</Text>
              </Panel>
            ))}
          </Flex>
        </Stack>
      </Stack>

      {sheetCase ? (
        <SessionRoomSheet
          session={sheetCase.entry}
          phase={sheetCase.phase}
          open
          onOpenChange={(next) => setSheetCase(next ? sheetCase : null)}
          onFullscreen={sheetCase.entry.chatId ? noop : null}
          onClose={noop}
          onOpenSessionSettings={noop}
          onOpenInvite={noop}
        />
      ) : null}
    </Box>
  );
}

/** Actual product surfaces with isolated fixture IDs; no real session is changed. */
const InGameSurfacesPreview = () => {
  const queryClient = useQueryClient();
  const formatDateRelative = useFormatDateRelative();
  const formatDuration = useFormatDuration();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['postCardPreview'],
    queryFn: () => getPlainPosts({ offset: 0, limit: 4, orders: [], filters: [] }),
    staleTime: 60_000,
  });
  const source = data?.records?.[0];
  const session = useMemo(
    () => ({
      id: SESSION_ID,
      startedAt: minutesFromNow(-10),
      expiredAt: minutesFromNow(50),
      status: 'LIVE',
      teamLimit: 4,
      acceptedMembersCount: 2,
      discordVoice: true,
      voiceMode: 'IN_GAME',
      voiceRoom: { connectedCount: 3, status: 'OPEN' },
      members: members.slice(0, 2).map((member) => ({
        memberId: member.memberId,
        status: member.status,
        userData: { displayName: member.displayName, avatar: null, socials: [] },
      })),
    }),
    []
  );
  const post = useMemo<Post | null>(
    () =>
      source?.userData
        ? {
            ...source,
            id: POST_ID,
            gameId: GAME_ID,
            userId: HOST_ID,
            text: 'IN_GAME preview',
            userData: { ...source.userData, id: HOST_ID, displayName: 'Salt', avatar: null },
            activeSession: {
              ...session,
              status: 'LIVE',
              postId: POST_ID,
              createdAt: session.startedAt,
              updatedAt: session.startedAt,
              discordVoiceCount: 3,
              sessionChat: null,
              voiceRoom: { ...session.voiceRoom, maxParticipants: 4, participants: [] },
            },
          }
        : null,
    [session, source]
  );
  useEffect(() => {
    if (!post) return;
    // Витрина использует свою игру, сохраняя настоящий каталог, если он уже приехал.
    const game: Extract<Game, { __typename: 'Game' }> = {
      __typename: 'Game',
      id: GAME_ID,
      name: 'Apex Legends',
      slug: 'apex-legends',
      icon: '/preview/session-chats/apex-mark.svg',
      iconColor: 'fg.warning',
      logo: null,
      mobileImage: null,
      desktopImage: null,
      platformsIds: [],
      socialPreviewLeft: null,
      socialPreviewRight: null,
      session: true,
      providers: [],
    };
    queryClient.setQueryData<Game[]>(['games'], (catalog) =>
      catalog?.some((item) => item?.__typename === 'Game' && item.id === GAME_ID)
        ? catalog
        : [...(catalog ?? []), game]
    );
    queryClient.setQueryData(peerSessionsQueryKey(HOST_ID), {
      records: [post],
      hasMore: false,
      totalCount: 1,
    });
  }, [post, queryClient]);
  if (!post) return <Text>Loading preview fixture…</Text>;
  const hostData = { memberId: HOST_ID, displayName: 'Salt', avatar: null };
  const common = {
    session,
    postId: POST_ID,
    hostData,
    formatDateRelative,
    formatDuration,
    onAction: noop,
  };
  return (
    <Stack data-testid="in-game-surfaces-preview" p={{ base: 0, md: 6 }} gap={5} w="full" minW={0}>
      <Frame label="IN_GAME · Tuned post card">
        <PostPreviewTuned
          {...post}
          discordMembersCount={3}
          discordPublished={false}
          bookmarkDisabled
        />
      </Frame>
      <Frame label="IN_GAME · Peer session status">
        <PeerSessionIdentity
          displayName="Salt"
          session={{
            postId: POST_ID,
            sessionId: session.id ?? '',
            gameName: 'Apex Legends',
            shared: false,
            hostedByPeer: true,
          }}
          onOpen={noop}
        />
      </Frame>
      <Frame label="IN_GAME · Session history / profile / friends">
        <Stack gap={3}>
          <SessionCard {...common} config={statusConfig.LIVE} isOwnPost={false} />
          <CompactSessionRow {...common} />
          <SessionCard
            {...common}
            session={{
              ...session,
              status: 'COMPLETED',
              startedAt: minutesFromNow(-120),
              expiredAt: minutesFromNow(-60),
            }}
            config={statusConfig.COMPLETED}
            isOwnPost={false}
          />
        </Stack>
      </Frame>
      <Flex gap={3} wrap="wrap">
        <Button onClick={() => setDetailsOpen(true)}>Preview: session details</Button>
        <Button onClick={() => setInviteOpen(true)}>Preview: invite picker</Button>
      </Flex>
      <DialogRoot open={detailsOpen} onOpenChange={({ open }) => setDetailsOpen(open)}>
        <SessionDetailsDialog
          {...session}
          postId={POST_ID}
          sessionId={SESSION_ID}
          isOwnPost={false}
          hostData={hostData}
          hasDiscordRoom
          onAction={noop}
          onClose={() => setDetailsOpen(false)}
        />
      </DialogRoot>
      <InviteSessionPicker
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        sessions={[
          {
            id: SESSION_ID,
            gameId: post.gameId,
            status: 'LIVE',
            startedAt: session.startedAt,
            taken: 2,
            total: 4,
            voice: false,
            voiceMode: 'IN_GAME',
            memberIds: new Set(),
            accepted: session.members.map((member) => ({
              userId: member.memberId,
              ...member.userData,
            })),
          },
        ]}
        userId={THIRD_ID}
        displayName="Nova"
      />
    </Stack>
  );
};

/** Геометрия подключённого голоса без сети и разрешения на микрофон. */
function VoiceControlsPreview() {
  return (
    <Stack p={3} gap={5} w="full" minW={0}>
      <SectionHeading>Подключённый голос · настоящие элементы управления</SectionHeading>
      {(['mobile', 'desktop'] as const).map((density) => (
        <VoiceControlsRow key={density} density={density} />
      ))}
    </Stack>
  );
}

function VoiceControlsRow({ density }: { density: 'mobile' | 'desktop' }) {
  const { t } = useLingui();
  const [micEnabled, setMicEnabled] = useState(true);
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  return (
    <Stack gap={2}>
      <Text>{density === 'mobile' ? 'Телефон · цели 44 px' : 'Десктоп · цели 40 px'}</Text>
      <Panel variant="nested" maxW="400px" w="full" minW={0} p={2}>
        <Flex align="center" gap={1} minW={0}>
          <Box flex={1} minW={0}>
            <TierCounts
              accepted={3}
              capacity={5}
              compact
              count={{ kind: 'voice', capacity: 5, connected: 2, live: true }}
            />
          </Box>
          <IconButton
            variant="ghost"
            aria-label={t`Open chat`}
            boxSize={density === 'mobile' ? '44px' : '40px'}
            minW={density === 'mobile' ? '44px' : '40px'}
          >
            <CommentIcon />
          </IconButton>
          <Box w="1px" h={6} bg="border.subtle" flexShrink={0} />
          <TierMicCluster
            density={density}
            micEnabled={micEnabled}
            micDenied={false}
            onToggleMic={() => setMicEnabled(!micEnabled)}
            onRequestAccess={noop}
            listDevices={async () => []}
            onSelectDevice={noop}
            devicePickerOpen={devicePickerOpen}
            onDevicePickerOpenChange={setDevicePickerOpen}
            onLeave={noop}
          />
        </Flex>
      </Panel>
    </Stack>
  );
}

/** Настоящая панель прямого звонка; медиа и сервер в изолированном превью не подключаются. */
const DirectCallPreview = () => {
  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(false);
  const [denied, setDenied] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [mini, setMini] = useState(false);
  const room = {
    remoteCameraRef: { current: null },
    remoteScreenRef: { current: null },
    localVideoRef: { current: null },
    localScreenRef: { current: null },
    connectionState: reconnecting ? 'reconnecting' : 'connected',
    remoteJoined: true,
    remoteVideoOn: false,
    remoteScreenOn: false,
    remoteMicOn: true,
    localSpeaking: false,
    remoteSpeaking: false,
    peerGone: false,
    screenSharing: false,
    screenSharePending: false,
    screenShareHint: null,
    mediaError: denied,
    camDenied: false,
    audioBlocked,
    microphoneTrack: null,
    unblockAudio: async () => {
      setAudioBlocked(false);
    },
    requestMicAccess: async () => {
      setDenied(false);
      return true;
    },
    requestCameraAccess: async () => true,
    toggleScreenShare: async () => {},
    refreshDevices: async () => {},
    switchDevice: async () => {},
    devices: {
      microphones: [
        { deviceId: 'mic-1', label: 'USB Microphone / Очень длинное название микрофона' },
      ],
      speakers: [{ deviceId: 'speaker-1', label: 'External speakers' }],
      cameras: [{ deviceId: 'camera-1', label: 'HD Camera / Очень длинное название камеры' }],
      activeMicrophoneId: 'mic-1',
      activeSpeakerId: 'speaker-1',
      activeCameraId: 'camera-1',
      speakerSelectionSupported: true,
    },
  } as unknown as ReturnType<typeof useCallRoom>;
  return (
    <Stack p={3} gap={4} minW={0} w="full">
      <SectionHeading>Прямой звонок</SectionHeading>
      <Button aria-label="Preview: direct mini" onClick={() => setMini(!mini)}>
        Мини-звонок
      </Button>
      {mini ? (
        <Box data-testid="direct-mini-preview">
          <CallMini
            peerName="Очень длинное имя собеседника Teamplay"
            peerAvatar={null}
            statusText={reconnecting ? 'Reconnecting…' : '12:34'}
            room={room}
            micEnabled={micEnabled}
            anchor="bottom"
            onToggleMic={() => setMicEnabled(!micEnabled)}
            onHangup={noop}
            onExpand={() => setMini(false)}
          />
        </Box>
      ) : null}
      <Box w="full" maxW="400px" minW={0} data-testid="direct-call-preview">
        <CallStage
          call={{
            callId: 'preview-call',
            chatId: 'preview-chat',
            roomName: 'preview',
            peer: {
              userId: 'preview-peer',
              displayName: 'Очень длинное имя собеседника Teamplay',
              avatar: null,
            },
            startedByMe: true,
            withVideo: false,
            livekitUrl: null,
            token: null,
            acceptedAt: Date.now(),
            ringingSince: null,
          }}
          variant="floating"
          phase="active"
          duration="12:34"
          micEnabled={micEnabled}
          camEnabled={camEnabled}
          onToggleMic={() => setMicEnabled(!micEnabled)}
          onToggleCam={() => setCamEnabled(!camEnabled)}
          onHangup={noop}
          room={room}
        />
      </Box>
      <IconButton aria-label="Preview: microphone denied" onClick={() => setDenied(!denied)}>
        <CommentIcon />
      </IconButton>
      <IconButton
        aria-label="Preview: audio blocked"
        onClick={() => setAudioBlocked(!audioBlocked)}
      >
        <CommentIcon />
      </IconButton>
      <IconButton
        aria-label="Preview: reconnecting call"
        onClick={() => setReconnecting(!reconnecting)}
      >
        <CommentIcon />
      </IconButton>
    </Stack>
  );
};

function SessionRoomPreviewFixture() {
  const search = Route.useSearch();
  const { audio } = search;
  const previewI18n = useMemo(
    () => setupI18n({ locale: search.lang, messages: { ru: ruMessages, en: enMessages } }),
    [search.lang]
  );
  // Изолированный кеш для визуальной проверки чата участника. Профиль и сессии пользователя не меняются.
  const squadClient = useMemo(() => {
    const client = new QueryClient({
      defaultOptions: { queries: { retry: false, staleTime: Infinity } },
    });
    // Хуки могут запросить refetch при монтировании: фикстуры не должны заменяться
    // ответом сервера для несуществующей сессии (тот же подход у session-compare).
    const defaultQueryOptions = client.defaultQueryOptions.bind(client);
    client.defaultQueryOptions = (options) => ({
      ...defaultQueryOptions(options),
      enabled: false,
      staleTime: Infinity,
      gcTime: Infinity,
      retry: false,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      queryFn: async () => {
        throw new Error('Session room fixtures cannot access the network');
      },
    });
    client.setQueryData(['me'], {
      __typename: 'CurrentUser',
      id: search.own ? HOST_ID : THIRD_ID,
      displayName: 'Kir',
      avatar: null,
      deleted: false,
      oauth: { discord: search.squad === 'discordLink' ? null : { id: 'preview-discord' } },
    });
    const liveSession = entry({ sessionId: '22222222-2222-4222-8222-222222222221' });
    client.setQueryData(['post', POST_ID], {
      __typename: 'Post',
      id: POST_ID,
      gameId: GAME_ID,
      userId: HOST_ID,
      userData: { id: HOST_ID, displayName: 'Salt', avatar: null },
      platformsIds: [],
      languages: [],
      data: [],
      mic: true,
      activeSession: { ...liveSession, id: liveSession.sessionId },
      sessions: [{ ...liveSession, id: liveSession.sessionId }],
    });
    client.setQueryData(['games'], [{ __typename: 'Game', ...liveSession.game }]);
    client.setQueryData(['platforms'], []);
    for (const postId of [
      POST_ID,
      POST_ID_UPCOMING,
      POST_ID_ENDED,
      '1111cccc-1111-4111-8111-111111111111',
    ]) {
      client.setQueryData(['discordRoom', postId], {
        channelInviteLink: 'preview',
        realtimeMembers: [{ id: HOST_ID }],
      });
    }
    return client;
  }, [search.own, search.squad]);
  const audioBridge = useMemo(() => {
    const headset = 'AirPods Pro Артёма - очень длинное название игровой гарнитуры';
    let snapshot: NativeAudioRoutes = {
      supportsSystemPicker: audio !== 'legacy',
      inputs: [
        { id: 'builtin', name: 'iPhone Microphone', builtIn: true },
        { id: 'headset', name: headset, builtIn: false },
      ],
      routes: [
        { id: 'automatic', kind: 'automatic', name: '' },
        { id: 'speaker', kind: 'speaker', name: '' },
        { id: 'receiver', kind: 'receiver', name: '' },
        { id: 'input:headset', kind: 'device', name: headset },
      ],
      inputId: 'headset',
      routeId: 'input:headset',
      currentInputs: [headset],
      currentOutputs: [headset],
    };
    if (audio === 'empty')
      snapshot = {
        inputs: [],
        routes: [],
        inputId: '',
        routeId: 'automatic',
        currentInputs: [],
        currentOutputs: ['iPhone Speaker'],
      };
    const listeners = new Set<(routes: NativeAudioRoutes) => void>();
    return {
      read: async () => {
        if (audio === 'loading') await new Promise((resolve) => setTimeout(resolve, 3000));
        if (audio === 'error') throw new Error('Preview: route query failed');
        return audio === 'old' ? null : snapshot;
      },
      subscribe: async (listener: (routes: NativeAudioRoutes) => void) => {
        listeners.add(listener);
        return () => {
          listeners.delete(listener);
        };
      },
      select: async (selection: NativeAudioSelection) => {
        await new Promise((resolve) => setTimeout(resolve, 350));
        if (audio === 'select-error') throw new Error('Preview: device disconnected');
        const external = selection.id === 'headset' || selection.id === 'input:headset';
        snapshot = {
          ...snapshot,
          inputId: external ? 'headset' : 'builtin',
          routeId: external
            ? 'input:headset'
            : selection.kind === 'route'
              ? selection.id
              : 'receiver',
          currentInputs: [external ? headset : 'iPhone Microphone'],
          currentOutputs: [
            external ? headset : selection.id === 'speaker' ? 'iPhone Speaker' : 'iPhone Receiver',
          ],
        };
        for (const listener of listeners) listener(snapshot);
        return snapshot;
      },
      openPicker: async () => {
        if (audio === 'select-error') throw new Error('Preview: system picker unavailable');
        // The real iOS picker is exercised on a device; the preview only proves the menu closes.
        return snapshot;
      },
    };
  }, [audio]);
  const audioContainerRef = useRef<HTMLDivElement | null>(null);
  const owner: VoiceRoomOwner = {
    audioContainerRef,
    micDenied: search.squad === 'micDenied',
    micLevel: { level: 0.6, listening: true, heard: true, failed: false },
    microphoneTrack: null,
    requestMicAccess: async () => true,
    audioBlocked: search.squad === 'audioBlocked',
    unblockAudio: async () => {},
    leave: async () => {},
    setParticipantVolume: noop,
    setParticipantMuted: noop,
    listDevices: async () => [],
    switchDevice: async () => {},
    activeDeviceIds: {},
    refreshDeviceIds: noop,
  };
  if (import.meta.env.DEV && search.notification) {
    return (
      <I18nProvider i18n={previewI18n}>
        <QueryClientProvider client={squadClient}>
          <NotificationSummaryPreview
            key={JSON.stringify(search)}
            state={search.notification}
            long={search.long}
          />
        </QueryClientProvider>
      </I18nProvider>
    );
  }
  if (import.meta.env.DEV && search.peer)
    return (
      <VoiceRoomOwnerContext.Provider value={owner}>
        <PeerSessionPreview key={JSON.stringify(search)} {...search} />
      </VoiceRoomOwnerContext.Provider>
    );
  const preview = (
    <VoiceRoomOwnerContext.Provider value={owner}>
      <SessionRoomPreview />
    </VoiceRoomOwnerContext.Provider>
  );
  if (search.squad)
    return (
      <I18nProvider i18n={previewI18n}>
        <QueryClientProvider client={squadClient}>{preview}</QueryClientProvider>
      </I18nProvider>
    );
  if (search.stability) return <I18nProvider i18n={previewI18n}>{preview}</I18nProvider>;
  return audio ? (
    <NativeAudioRoutesContext.Provider value={audioBridge}>
      {preview}
    </NativeAudioRoutesContext.Provider>
  ) : (
    preview
  );
}

function NotificationSummaryPreview({ state, long }: { state: string; long: boolean }) {
  const [open, setOpen] = useState(true);
  return (
    <DialogRoot
      size="md"
      initialFocusEl="body"
      open={open}
      onOpenChange={({ open }) => setOpen(open)}
    >
      <SessionNotificationSummaryContent
        summary={
          state === 'unavailable'
            ? null
            : {
                id: SESSION_ID,
                postId: POST_ID,
                gameId: GAME_ID,
                startedAt: '2026-10-07T10:00:00Z',
                expiredAt: '2026-10-07T12:00:00Z',
                status: state === 'cancelled' ? 'CANCELLED' : 'COMPLETED',
                viewerStatus: state === 'removed' ? 'REJECTED' : 'ACCEPTED',
              }
        }
        gameName={
          state === 'unavailable'
            ? null
            : long
              ? 'Counter-Strike 2: Competitive Premier with a very long game title'
              : 'Counter-Strike 2'
        }
        loading={state === 'loading'}
        error={state === 'error'}
        onRetry={noop}
        onClose={() => setOpen(false)}
      />
    </DialogRoot>
  );
}
